import { Box, makeStyles } from "@material-ui/core";
import { BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis, Tooltip, Bar, Text } from "recharts";

import { GraphSectionLayout } from "./GraphSectionLayout";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { fontFamily } from "../../../../app/theme";
import { CustomChartTick } from "./CustomTickChart";
import { ElectricityBillTooltip } from "./ElectricityBillTooltip";
import { SolarReport } from "../../../../api/Quote/SolarReport";
import { PriceFormat } from "../../../../components/typography/PriceFormat";

export const ElectricityBill = ({ solarReportData }: { solarReportData: SolarReport | null }) => {
  const { isMoreThanSm } = useBreakpoint();
  const classes = useStyles();
  const newBill = solarReportData ? Number(solarReportData.electricityEstimatedAnnualSpend) : 0;

  return (
    <GraphSectionLayout
      title={"Estimated Electricity Bill in Year 1"}
      description={
        "This is a comparison of your current annual electricity bill against the estimated calculation of your new annual electricity bill in the first year after you install Solar PV."
      }
      leftContent={
        <Box>
          <Box
            mt={isMoreThanSm ? "45px" : "23px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            fontSize={isMoreThanSm ? "14px" : "12px"}
            lineHeight={isMoreThanSm ? 1.71 : 2}
            fontWeight={500}
            letterSpacing={"0.32px"}
            color={"#4b5462"}
          >
            <Box>Old Annual Electricity Bill</Box>
            <Box>
              <PriceFormat value={Number(solarReportData?.electricityCurrentAnnualSpend)} />
            </Box>
          </Box>
          <Box
            mt={"10px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            fontSize={isMoreThanSm ? "14px" : "12px"}
            lineHeight={isMoreThanSm ? 1.71 : 2}
            fontWeight={500}
            letterSpacing={"0.32px"}
            color={"#4b5462"}
          >
            <Box>New Estimated Annual Electricity Bill</Box>
            <Box color={"#f7815c"}>
              <PriceFormat value={Number(newBill)} />
            </Box>
          </Box>
        </Box>
      }
      rightContent={
        <Box width={"100%"} height={"100%"}>
          <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
            <Box display={"flex"} alignItems={"center"} justifyContent={"center"} fontSize={"12px"} fontWeight={500}>
              <Box
                width={"11px"}
                height={"11px"}
                mr={"4px"}
                mb={"2px"}
                borderRadius={"50%"}
                border={"solid 1px rgba(70, 60, 87, 0.5)"}
                bgcolor={"rgba(22, 3, 55, 0.6)"}
              />
              Old Bill
            </Box>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
              fontSize={"12px"}
              fontWeight={500}
              ml={"26px"}
            >
              <Box
                width={"11px"}
                height={"11px"}
                mr={"4px"}
                mb={"2px"}
                borderRadius={"50%"}
                border={"1px solid #eb9b81"}
                bgcolor={"rgba(251, 192, 173, 0.6)"}
              />
              Estimated Bill
            </Box>
          </Box>
          <Box width={"100%"} height={"290px"} mt={"15px"}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                data={[
                  {
                    label: "Old Bill without Solar PV",
                    old: solarReportData?.electricityCurrentAnnualSpend || 0,
                    estimated: 0
                  },
                  { label: "Estimated Bill with Solar PV", old: 0, estimated: newBill }
                ]}
                className={classes.chart}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }}
                style={{ overflow: "inherit" }}
              >
                <CartesianGrid vertical={false} stroke="#efefef" />
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickCount={2}
                  tickLine={false}
                  interval={0}
                  tick={({ x, y, payload }: any) => (
                    <g transform={`translate(${x},${y})`}>
                      <Text
                        x={0}
                        y={0}
                        dy="16px"
                        textAnchor="middle"
                        fill="#4c5878"
                        color="#4c5878"
                        fontSize={"12px"}
                        fontFamily={fontFamily.Axiforma}
                        width={isMoreThanSm ? undefined : 75}
                        verticalAnchor={"start"}
                      >
                        {payload.value}
                      </Text>
                    </g>
                  )}
                />
                <Tooltip
                  content={<ElectricityBillTooltip />}
                  wrapperStyle={{ outline: "none" }}
                  cursor={{ fill: "none" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tickCount={7}
                  width={isMoreThanSm ? undefined : 30}
                  tick={<CustomChartTick />}
                />
                <Bar dataKey="old" stackId="a" fill="rgba(22, 3, 55, 0.6)" />
                <Bar dataKey="estimated" stackId="a" fill="#fdd9ce" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      }
    />
  );
};

const useStyles = makeStyles((theme) => ({
  chart: {
    "& .recharts-surface": {
      overflow: "inherit"
    }
  },
  labelYear: {
    "& tspan": {
      dy: "-20px"
    }
  }
}));
